/**
 * @file DeletedPagesDialog.tsx
 * @description «Удалённые страницы» (phase19.16 Р3а; SB128, SB63): страницы
 * хранятся 12 месяцев после удаления вместе с версиями и черновиком;
 * «Восстановить» возвращает страницу скрытой со своим адресом, занятый адрес
 * — просит новый (`RestoreSlugForm`). Восстанавливает сотрудник с правом
 * публикации контента. До sm — на весь экран.
 */

import { RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../ui/dialog";
import { EmptyState } from "../../ui/EmptyState";
import { useNotify } from "../../ui/NotifyProvider";
import { Skeleton } from "../../ui/skeleton";
import { useBuilder } from "../builder-context";
import { RestoreSlugForm } from "./RestoreSlugForm";
import { useDeletedPages, type RestoreOutcome } from "../../../hooks/site-builder/useDeletedPages";
import { formatShopDate } from "../../../lib/shop-time";

export function DeletedPagesDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const b = useBuilder();
  const notify = useNotify();
  const deleted = useDeletedPages(open);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Восстановление ждёт новый адрес: страница и занятый адрес.
  const [slugAsk, setSlugAsk] = useState<{ pageId: string; takenSlug: string } | null>(null);

  async function restore(pageId: string, slug?: string): Promise<RestoreOutcome> {
    setBusyId(pageId);
    const res = await deleted.restore(pageId, slug);
    setBusyId(null);
    if (res.kind === "slug") {
      setSlugAsk({ pageId, takenSlug: res.takenSlug });
      return res;
    }
    if (res.kind === "error") {
      // Ошибку адреса покажет форма под полем; остальное — тостом.
      if (!slug) notify.error(res.text);
      return res;
    }
    setSlugAsk(null);
    await b.pages.reloadList();
    onClose();
    notify.success(`Страница «${res.page.title}» восстановлена — она скрыта, пока вы её не покажете`);
    await b.select({ pageId: res.page.id, screen: null, panel: null, sel: { kind: "page" } });
    return res;
  }

  const items = deleted.items ?? [];
  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : null)}>
      <DialogContent fullScreen className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Удалённые страницы</DialogTitle>
          <DialogDescription>Страницы хранятся 12 месяцев после удаления вместе с версиями. Восстановленная страница вернётся скрытой.</DialogDescription>
        </DialogHeader>
        {deleted.error ? <p className="type-body text-destructive">{deleted.error}</p> : null}
        {deleted.loading ? <Skeleton className="h-24 rounded-xl" aria-label="Загружаем удалённые страницы" /> : null}
        {!deleted.loading && items.length === 0 && !deleted.error ? <EmptyState icon={Trash2} title="Удалённых страниц нет" /> : null}
        <ul className="flex flex-col gap-2">
          {items.map((p) => (
            <li key={p.id} className="flex flex-col gap-2 rounded-xl bg-surface-low p-3">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="type-body truncate font-medium text-on-surface" title={p.title}>
                    {p.title}
                  </span>
                  <span className="type-caption text-on-surface-variant">
                    {p.deletedAt ? `удалена ${formatShopDate(p.deletedAt)}` : "удалена"} · блоков {p.blocksCount}
                  </span>
                </div>
                {b.canPublishPages ? (
                  <Button variant="outline" className="shrink-0" loading={busyId === p.id && slugAsk?.pageId !== p.id} onClick={() => void restore(p.id)}>
                    <RotateCcw aria-hidden />
                    Восстановить
                  </Button>
                ) : null}
              </div>
              {slugAsk?.pageId === p.id ? (
                <RestoreSlugForm takenSlug={slugAsk.takenSlug} busy={busyId === p.id} onCancel={() => setSlugAsk(null)} onSubmit={(slug) => restore(p.id, slug)} />
              ) : null}
            </li>
          ))}
        </ul>
        {b.canPublishPages ? null : <p className="type-caption text-on-surface-variant">Восстановить страницу может сотрудник с правом публикации контента.</p>}
      </DialogContent>
    </Dialog>
  );
}
